import {
  useAuditLogContext,
  useAuditLogPermissionState,
  useEntityPropertyKeyNormalized,
} from "@ryde/redux";
import {
  AUDIT_LOG_MUTED_KEYS,
  AUDIT_LOG_NUMERIC_DELTA_KEYS,
  checkPropertyValueNill,
  CourseMetadataKeys,
  EntityPropertyChanges,
  getAuditLogNumericDelta,
  getEntityPropertySearchTexts,
  IEntityPropertyArrayPrimitive,
  IEntityPropertyPrimitive,
  NO_VALUE_DISPLAY,
  RydeUiUnit,
} from "@ryde/shared";

/** What one changed field shows: its label, old and new text, the numeric difference, and whether it shows at all. */
export const useEntityPropertyValue = ({
  entity,
  entityProperty,
}: {
  entity?: RydeUiUnit;
  entityProperty: IEntityPropertyPrimitive | IEntityPropertyArrayPrimitive;
}) => {
  const { key, path, changeType, oldValue, value } = entityProperty;
  const { searchValue, showSearchResultsOnly } = useAuditLogContext();
  const propertyKey = useEntityPropertyKeyNormalized({
    entity,
    entityProperty,
  });
  const { getPermissionStateText } = useAuditLogPermissionState({
    entity,
    path,
  });

  const isEmpty =
    checkPropertyValueNill(oldValue) && checkPropertyValueNill(value);
  // In search results only mode, only show properties that contain the search term.
  const isFilteredOut =
    showSearchResultsOnly &&
    !!searchValue &&
    !getEntityPropertySearchTexts(entityProperty).some(text =>
      text.toLowerCase().includes(searchValue.toLowerCase()),
    );

  return {
    isHidden: isEmpty || isFilteredOut,
    propertyKey,
    // Only the start of an encoded polyline is worth seeing, so it stays on one line.
    rootClassName:
      key === CourseMetadataKeys.routePolyline
        ? "entityProperty entityProperty--single-line"
        : "entityProperty",
    keyClassName: AUDIT_LOG_MUTED_KEYS.has(key)
      ? "entityProperty__key entityProperty__key--muted"
      : "entityProperty__key",
    oldText:
      oldValue && !checkPropertyValueNill(oldValue)
        ? getPermissionStateText(oldValue)
        : undefined,
    removedText: getPermissionStateText(String(oldValue)),
    newText: value === "" ? NO_VALUE_DISPLAY : getPermissionStateText(value),
    numericDelta:
      changeType === EntityPropertyChanges.Changed &&
      AUDIT_LOG_NUMERIC_DELTA_KEYS.has(key)
        ? getAuditLogNumericDelta({ oldValue, value })
        : undefined,
  };
};
